import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuthTheme, getAuthStyles, injectAuthKeyframes } from './AuthTheme';

injectAuthKeyframes();

const DEPARTMENTS = [
  "Architecture Engineering","Basic Sciences And Related Studies","Bio-Medical Engineering",
  "Chemical Engineering","City And Regional Planning","Civil Engineering",
  "Computer Systems Engineering","Electrical Engineering","Electronics Engineering",
  "Industrial Engineering","Mechanical Engineering","Mechatronic Engineering",
  "Metallurgy And Materials Engineering","Mining Engineering",
  "Petroleum And Natural Gas Engineering","Software Engineering",
  "Telecommunication Engineering","Textile Engineering"
];

const PARTICLES = [
  { top: '10%', left: '8%', animationDelay: '0s' },
  { top: '20%', right: '10%', animationDelay: '1s' },
  { top: '60%', left: '5%', animationDelay: '2s' },
  { bottom: '20%', right: '8%', animationDelay: '0.5s' },
  { bottom: '10%', left: '20%', animationDelay: '1.5s' },
  { top: '45%', right: '5%', animationDelay: '2.5s' },
];

export default function FacultyRegister() {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '', department: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  const { dark } = useAuthTheme();
  const s = getAuthStyles();

  const handleChange = e => { setFormData({ ...formData, [e.target.name]: e.target.value }); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    if (!formData.name.trim()) { setError('Name is required'); return; }
    if (!formData.email.toLowerCase().includes('@faculty.')) { setError('Use your faculty email: xyz@faculty.edu'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (!formData.department) { setError('Please select your department'); return; }
    const result = await register({
      name: formData.name, email: formData.email, password: formData.password,
      role: 'faculty', department: formData.department
    });
    if (result.success) { setSuccess('Account created! Redirecting to login...'); setTimeout(() => navigate('/login/faculty'), 2000); }
    else setError(result.error || 'Registration failed. Please try again.');
  };

  

  return (
    <div style={s.page}>
      <div style={s.orb1} /><div style={s.orb2} /><div style={s.orb3} />
      <div style={s.particles}>
        {PARTICLES.map((p, i) => <div key={i} style={{ ...s.particle, ...p }} />)}
      </div>

      <div style={s.wrapWide}>
        <div style={s.brand} onClick={() => navigate('/')}>
          <div style={s.logoRing}><span style={s.logoEmoji}>👨‍🏫</span><div style={s.logoGlow} /></div>
          <h1 style={s.brandName}>UniResolve</h1>
          <p style={s.brandSub}>Faculty Registration</p>
        </div>

        <div style={s.card}>
          <div style={s.cardTop}><div style={s.cardTopBadge}>Create Faculty Account</div></div>
          <div style={{ ...s.cardBody, padding: '24px 28px 28px' }}>
            <div style={s.hintBox}>
              <span>📧</span>
              <span style={s.hintText}>Faculty email format: <strong>xyz@faculty.edu</strong></span>
            </div>
            {error && <div style={s.err}><span>{error}</span><button type="button" onClick={() => setError('')} style={s.errX}>x</button></div>}
            {success && <div style={s.ok}><span>{success}</span></div>}

            <form onSubmit={handleSubmit}>
              <div style={s.field}>
                <label style={s.label}>Full Name</label>
                <input name="name" type="text" value={formData.name} onChange={handleChange}
                  placeholder="Enter your full name" style={s.input} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Faculty Email</label>
                <input name="email" type="email" value={formData.email} onChange={handleChange}
                  placeholder="xyz@faculty.edu" style={s.input} required />
              </div>
              <div style={s.row2}>
                <div style={s.field}>
                  <label style={s.label}>Password</label>
                  <div style={s.pwWrap}>
                    <input name="password" type={showPw ? 'text' : 'password'} value={formData.password}
                      onChange={handleChange} placeholder="Min 6 characters" style={s.input} required />
                    <button type="button" onClick={() => setShowPw(!showPw)} style={s.pwBtn}>{showPw ? 'Hide' : 'Show'}</button>
                  </div>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Confirm Password</label>
                  <div style={s.pwWrap}>
                    <input name="confirmPassword" type={showCpw ? 'text' : 'password'} value={formData.confirmPassword}
                      onChange={handleChange} placeholder="Re-enter password" style={s.input} required />
                    <button type="button" onClick={() => setShowCpw(!showCpw)} style={s.pwBtn}>{showCpw ? 'Hide' : 'Show'}</button>
                  </div>
                </div>
              </div>
              <div style={s.field}>
                <label style={s.label}>Department</label>
                <select name="department" value={formData.department} onChange={handleChange} style={s.select} required>
                  <option value="">Select your department</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <button type="submit" disabled={loading} style={{ ...s.btn, ...(loading ? s.btnOff : {}) }}>
                {loading ? <span style={s.btnIn}><span style={s.spin} /> Creating Account...</span> : 'Create Faculty Account'}
              </button>
            </form>

            <div style={{ ...s.regRow, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
              <div><span style={s.regText}>Already have an account? </span><span style={s.regLink} onClick={() => navigate('/login/faculty')}>Sign in</span></div>
              <div><span style={s.regText}>Are you a student? </span><span style={s.regLink} onClick={() => navigate('/register/student')}>Register as Student</span></div>
            </div>
          </div>
        </div>
        <p style={s.footer}>2026 UniResolve</p>
      </div>
    </div>
  );
}
